import { useTranslation } from "gatsby-plugin-react-i18next";
import { useContext, useState } from "react";
import Gallery from "../../components/Gallery";
import GalleryGrid from "../../components/Gallery/components/GalleryGrid/GalleryGrid";
import GalleryGridColumn from "../../components/Gallery/components/GalleryGridColumn/GalleryGridColumn";
import PlayButton from "../../components/Gallery/components/PlayButton/PlayButton";
import VideoImage from "../../components/Gallery/components/VideoImage/VideoImage";
import GridItem from "../../components/general/GridItem";
import ImageContainer from "../../components/general/ImageContainers/ImageContainer";
import VideoModal from "../../components/general/VideoModal/VideoModal";
import { PageContext } from "../../templates/page/page.container";
import { getOriginalVimeoThumbnail } from "../../utils/vimeo/utils";
import { useIsTablet } from "../../hooks/use-is-screensize";
import Hotspots from "../../components/general/Hotspots";
import { mapHotspotsToHotspotsProps } from "../../utils/storyblok/product-variant";

const GalleryModule = ({
	video,
	upperLeftImage,
	lowerLeftImage,
	upperRightImage,
	lowerRightImage,
}: Queries.GalleryModuleFragment) => {
	const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
	const { publishedAt } = useContext(PageContext);
	const isTablet = useIsTablet();
	const { t } = useTranslation();

	return (
		<GridItem>
			<Gallery>
				{video &&
					video.map((v) => (
						<VideoImage
							key={v?._uid}
							image={
								<Hotspots
									name="hotpots"
									hotspots={
										v?.video?.filename || v?.vimeo?.vimeo_oembed
											? undefined
											: mapHotspotsToHotspotsProps(v?.hotspots)
									}
									productGallery
								>
									<ImageContainer
										imgSrc={
											(!isTablet && v?.mobileImage?.filename) ||
											v?.image?.filename ||
											getOriginalVimeoThumbnail(
												v?.vimeo?.vimeo_oembed?.response?.thumbnail_url,
											) ||
											undefined
										}
										alt={
											v?.image?.alt ||
											v?.vimeo?.vimeo_oembed?.response?.title ||
											undefined
										}
										objectFit="cover"
										sizes="(max-width: 768px) 80vw, 40vw"
									>
										<meta
											itemProp="thumbnailUrl"
											content={
												v?.image?.filename ||
												`https://vimeo.com/${v?.vimeo?.vimeo_oembed?.response?.video_id}` ||
												undefined
											}
										/>
										<meta itemProp="uploadDate" content={publishedAt || ""} />
										<meta
											itemProp="name"
											content={
												v?.image?.name ||
												v?.vimeo?.vimeo_oembed?.response?.title ||
												""
											}
										/>
										<meta
											itemProp="description"
											content={v?.image?.alt ?? undefined}
										/>
										<meta
											itemProp={v?.video?.filename ? "contentUrl" : "embedUrl"}
											content={
												v?.vimeo?.vimeo_oembed?.response?.video_id
													? `https://vimeo.com/${v.vimeo.vimeo_oembed.response.video_id}`
													: (v?.video?.filename ??
														`https://www.youtube.com/embed/${v?.youtubeEmbedId}?autoplay=1&mute=1`)
											}
										/>
										{(v?.video?.filename ||
											v?.vimeo?.vimeo_oembed ||
											v?.youtubeEmbedId) && (
											<PlayButton
												onClick={() => setIsVideoModalOpen(true)}
												ariaLabel={t("Play video")}
											/>
										)}
									</ImageContainer>
								</Hotspots>
							}
						/>
					))}
				<GalleryGrid>
					<GalleryGridColumn offset>
						<ImageContainer
							imgSrc={upperLeftImage?.filename || undefined}
							alt={upperLeftImage?.alt ?? undefined}
							ratio="portrait"
							objectFit="cover"
							sizes="(max-width: 768px) 40vw, 20vw"
						/>
						<ImageContainer
							imgSrc={lowerLeftImage?.filename || undefined}
							alt={lowerLeftImage?.alt ?? undefined}
							ratio="portrait"
							objectFit="cover"
							sizes="(max-width: 768px) 40vw, 20vw"
						/>
					</GalleryGridColumn>
					<GalleryGridColumn>
						<ImageContainer
							imgSrc={upperRightImage?.filename || undefined}
							alt={upperRightImage?.alt ?? undefined}
							ratio="portrait"
							objectFit="cover"
							sizes="(max-width: 768px) 40vw, 20vw"
						/>
						<ImageContainer
							imgSrc={lowerRightImage?.filename || undefined}
							alt={lowerRightImage?.alt ?? undefined}
							ratio="portrait"
							objectFit="cover"
							sizes="(max-width: 768px) 40vw, 20vw"
						/>
					</GalleryGridColumn>
				</GalleryGrid>
			</Gallery>
			{video &&
				video.map((v) => (
					<VideoModal
						key={v?._uid}
						isOpen={isVideoModalOpen}
						videoSrc={v?.video?.filename || ""}
						youtubeEmbedId={v?.youtubeEmbedId || ""}
						vimeoResponse={v?.vimeo?.vimeo_oembed?.response ?? undefined}
						onClose={() => setIsVideoModalOpen(false)}
						label={t("Back")}
					/>
				))}
		</GridItem>
	);
};
export default GalleryModule;
